import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Copy, Gift, Link as LinkIcon, Loader2 } from "lucide-react";
import { adminAuthHeader } from "@/hooks/useAdminAuth";
import { extractApiError } from "@/utils/apiError";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const KIND_META = {
  bundle_234: {
    label: "🎁 Genera link sblocco L2+L3+L4 gratis",
    confirm: (name) =>
      `Generare un magic link che permette a ${name || "questo utente"} ` +
      "di sbloccare GRATIS le Lezioni 2, 3 e 4 con un click?\n\n" +
      "Il link auto-logga l'utente, apre una landing celebrativa con un CTA, " +
      "e SOLO al click finale lo sblocco viene applicato. Copierai il link.",
    successToast: "Link generato e copiato negli appunti",
    bg: "#eff6ff", border: "#93c5fd", color: "#1e40af",
  },
  full_course: {
    label: "🎁 Genera link sblocco TUTTO gratis",
    confirm: (name) =>
      `Generare un magic link che permette a ${name || "questo utente"} ` +
      "di sbloccare GRATIS TUTTO il corso (9 lezioni) con un click?\n\n" +
      "Il link auto-logga l'utente, apre una landing celebrativa con un CTA, " +
      "e SOLO al click finale lo sblocco viene applicato. Copierai il link.",
    successToast: "Link generato e copiato negli appunti",
    bg: "#fff7ed", border: "#fdba74", color: "#9a3412",
  },
};

/**
 * Replaces the old immediate-unlock buttons. Now generates a magic link
 * (auto-login + tracking) that the admin sends to the lead via WhatsApp.
 * The user lands on /sblocco-gratuito/{kind} and must click a CTA to claim.
 */
export default function GenerateUnlockLinkButton({ userId, userName, kind, onGenerated }) {
  const [loading, setLoading] = useState(false);
  const meta = KIND_META[kind];
  if (!meta) return null;

  const handleClick = async () => {
    if (loading) return;
    if (!window.confirm(meta.confirm(userName))) return;
    setLoading(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/users/${userId}/generate-unlock-link`,
        { kind, hours: 168 },
        { headers: adminAuthHeader() },
      );
      // Copy to clipboard so admin can paste straight into WhatsApp/Telegram.
      try {
        await navigator.clipboard.writeText(data.url);
      } catch {
        // Clipboard API blocked → fall back to a prompt window.
        window.prompt("Link generato — copialo manualmente:", data.url);
      }
      toast.success(meta.successToast, {
        description: `Scade tra 7 giorni · tracking ${data.tracking_id.slice(0, 8)}`,
      });
      onGenerated?.(data);
    } catch (err) {
      toast.error(extractApiError(err, "Errore generazione link"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={loading}
      data-testid={`gen-unlock-link-${kind}-${userId}`}
      className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-md border transition disabled:opacity-50"
      style={{ background: meta.bg, borderColor: meta.border, color: meta.color }}
      title={meta.label}
    >
      {loading
        ? <><Loader2 size={12} className="animate-spin" /> Genero…</>
        : <><LinkIcon size={12} /> {meta.label}</>}
    </button>
  );
}
